import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { formatCurrency } from '../../lib/i18n';
import {
  Users,
  Wrench,
  Package,
  TrendingUp,
  AlertTriangle,
  Clock,
  CheckCircle,
  Plus,
  ArrowRight,
  Bell,
  MessageSquare,
  CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  waiting_parts: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  delivered: 'bg-slate-100 text-slate-800'
};

const statusLabels = {
  en: {
    pending: 'Pending',
    in_progress: 'In Progress',
    waiting_parts: 'Waiting for Parts/Hold',
    completed: 'Completed',
    delivered: 'Delivered'
  },
  bn: {
    pending: 'অপেক্ষমান',
    in_progress: 'চলমান',
    waiting_parts: 'পার্টসের জন্য অপেক্ষা/হোল্ড',
    completed: 'সম্পন্ন',
    delivered: 'ডেলিভারি হয়েছে'
  }
};

export default function DashboardPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { garageSlug } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marketingAlerts, setMarketingAlerts] = useState({ due_reminders: 0, pending_requests: 0 });
  const [reminderStats, setReminderStats] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Build base path for navigation
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/reports/dashboard`);
      setStats(response.data);
      // Fetch marketing alerts (non-blocking)
      Promise.all([
        axios.get(`${API}/marketing/reminders/due`).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/parts-requests?status=pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/reminders/stats`).catch(() => ({ data: null }))
      ]).then(([dueRes, reqRes, statsRes]) => {
        setMarketingAlerts({ due_reminders: dueRes.data?.length || 0, pending_requests: reqRes.data?.length || 0 });
        if (statsRes.data) setReminderStats(statsRes.data);
      });
      // Fetch subscription info for expiry warning (non-blocking)
      axios.get(`${API}/subscription/my`).then(res => setSubscriptionInfo(res.data)).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          {/* Welcome banner skeleton */}
          <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl p-6 animate-pulse">
            <div className="h-7 w-64 bg-blue-700/50 rounded mb-2" />
            <div className="h-4 w-80 bg-blue-700/40 rounded" />
          </div>

          {/* Stat cards skeleton */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4 md:p-6 animate-pulse">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-200 rounded-lg mb-3" />
                  <div className="h-8 w-16 bg-slate-200 rounded mb-1" />
                  <div className="h-4 w-24 bg-slate-100 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions + Recent Jobs skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1">
              <CardHeader><div className="h-5 w-28 bg-slate-200 rounded animate-pulse" /></CardHeader>
              <CardContent className="space-y-3 animate-pulse">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded-lg" />
                ))}
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader><div className="h-5 w-28 bg-slate-200 rounded animate-pulse" /></CardHeader>
              <CardContent className="space-y-3 animate-pulse">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-slate-200 rounded mb-2" />
                      <div className="h-3 w-48 bg-slate-100 rounded" />
                    </div>
                    <div className="h-5 w-16 bg-slate-200 rounded" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Monthly stats skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <Card key={i}>
                <CardHeader><div className="h-5 w-32 bg-slate-200 rounded animate-pulse" /></CardHeader>
                <CardContent className="animate-pulse">
                  <div className="h-10 w-40 bg-slate-200 rounded mb-2" />
                  <div className="h-3 w-20 bg-slate-100 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: language === 'en' ? "Today's Jobs" : 'আজকের জবস',
      value: stats?.today_jobs || 0,
      icon: Wrench,
      color: 'bg-blue-500',
      link: `${basePath}/jobs`
    },
    {
      title: t('pending_jobs'),
      value: stats?.pending_jobs || 0,
      icon: Clock,
      color: 'bg-orange-500',
      link: `${basePath}/jobs?status=pending`
    },
    {
      title: t('today_revenue'),
      value: formatCurrency(stats?.today_revenue || 0, language),
      icon: TrendingUp,
      color: 'bg-green-500',
      link: `${basePath}/reports`
    },
    {
      title: t('low_stock_alert'),
      value: stats?.low_stock_count || 0,
      icon: AlertTriangle,
      color: stats?.low_stock_count > 0 ? 'bg-red-500' : 'bg-slate-400',
      link: `${basePath}/inventory?low_stock=true`
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Subscription Expiry Warning */}
        {(() => {
          if (!subscriptionInfo) return null;
          const endDate = subscriptionInfo.subscription_end_date ? new Date(subscriptionInfo.subscription_end_date) : null;
          const trialEnd = subscriptionInfo.trial_end_date ? new Date(subscriptionInfo.trial_end_date) : null;
          const isInTrial = trialEnd && trialEnd > new Date();
          const relevantEnd = isInTrial ? trialEnd : endDate;
          if (!relevantEnd) return null;
          const daysLeft = Math.ceil((relevantEnd - new Date()) / (1000 * 60 * 60 * 24));
          if (daysLeft > 7) return null;
          const isExpired = daysLeft <= 0;
          return (
            <Link to={`${basePath}/settings?tab=subscription`} className="block">
              <div className={`flex items-center gap-3 p-4 rounded-xl border transition-colors ${isExpired ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-amber-50 border-amber-200 hover:border-amber-400'}`}>
                <AlertTriangle className={`w-5 h-5 shrink-0 ${isExpired ? 'text-red-500' : 'text-amber-500'}`} />
                <div className="flex-1">
                  <p className={`text-sm font-bold ${isExpired ? 'text-red-700' : 'text-amber-700'}`}>
                    {isExpired
                      ? (isInTrial
                        ? (language === 'en' ? 'Your free trial has expired!' : '\u0986\u09aa\u09a8\u09be\u09b0 \u09ab\u09cd\u09b0\u09bf \u099f\u09cd\u09b0\u09be\u09df\u09be\u09b2 \u09ae\u09c7\u09df\u09be\u09a6\u09cb\u09a4\u09cd\u09a4\u09c0\u09b0\u09cd\u09a3!')
                        : (language === 'en' ? 'Your subscription has expired!' : '\u0986\u09aa\u09a8\u09be\u09b0 \u09b8\u09be\u09ac\u09b8\u09cd\u0995\u09cd\u09b0\u09bf\u09aa\u09b6\u09a8 \u09ae\u09c7\u09df\u09be\u09a6\u09cb\u09a4\u09cd\u09a4\u09c0\u09b0\u09cd\u09a3!'))
                      : (isInTrial
                        ? (language === 'en' ? `Your free trial expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}` : `\u0986\u09aa\u09a8\u09be\u09b0 \u09ab\u09cd\u09b0\u09bf \u099f\u09cd\u09b0\u09be\u09df\u09be\u09b2 ${daysLeft} \u09a6\u09bf\u09a8\u09c7 \u09ae\u09c7\u09df\u09be\u09a6\u09cb\u09a4\u09cd\u09a4\u09c0\u09b0\u09cd\u09a3 \u09b9\u09ac\u09c7`)
                        : (language === 'en' ? `Your subscription expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}` : `\u0986\u09aa\u09a8\u09be\u09b0 \u09b8\u09be\u09ac\u09b8\u09cd\u0995\u09cd\u09b0\u09bf\u09aa\u09b6\u09a8 ${daysLeft} \u09a6\u09bf\u09a8\u09c7 \u09ae\u09c7\u09df\u09be\u09a6\u09cb\u09a4\u09cd\u09a4\u09c0\u09b0\u09cd\u09a3 \u09b9\u09ac\u09c7`))
                    }
                  </p>
                  <p className={`text-xs mt-0.5 ${isExpired ? 'text-red-500' : 'text-amber-500'}`}>
                    {isInTrial
                      ? (language === 'en' ? 'Go to Settings → Subscription to start a paid plan.' : '\u09b8\u09c7\u099f\u09bf\u0982\u09b8 → \u09b8\u09be\u09ac\u09b8\u09cd\u0995\u09cd\u09b0\u09bf\u09aa\u09b6\u09a8\u09c7 \u0997\u09bf\u09df\u09c7 \u09aa\u09c7\u0987\u09a1 \u09aa\u09cd\u09b2\u09cd\u09af\u09be\u09a8 \u09b6\u09c1\u09b0\u09c1 \u0995\u09b0\u09c1\u09a8\u0964')
                      : (language === 'en' ? 'Go to Settings → Subscription to renew your plan.' : '\u09b8\u09c7\u099f\u09bf\u0982\u09b8 → \u09b8\u09be\u09ac\u09b8\u09cd\u0995\u09cd\u09b0\u09bf\u09aa\u09b6\u09a8\u09c7 \u0997\u09bf\u09df\u09c7 \u09b0\u09bf\u09a8\u09bf\u0989 \u0995\u09b0\u09c1\u09a8\u0964')}
                  </p>
                </div>
                <div className={`flex items-center gap-1 text-sm font-semibold ${isExpired ? 'text-red-600' : 'text-amber-600'}`}>
                  {isInTrial
                    ? <><CreditCard className="w-4 h-4" />{language === 'en' ? 'Start Paid Plan' : '\u09aa\u09c7\u0987\u09a1 \u09aa\u09cd\u09b2\u09cd\u09af\u09be\u09a8'}</>
                    : <><ArrowRight className="w-4 h-4" />{language === 'en' ? 'Renew' : '\u09b0\u09bf\u09a8\u09bf\u0989'}</>}
                </div>
              </div>
            </Link>
          );
        })()}

        {/* Welcome Message */}
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl p-6 text-white">
          <h2 className="text-2xl font-bold font-['Barlow_Condensed'] mb-2">
            {language === 'en' ? `Welcome back, ${user?.name}!` : `স্বাগতম, ${user?.name}!`}
          </h2>
          <p className="text-blue-100">
            {language === 'en' 
              ? `You have ${stats?.pending_jobs || 0} pending jobs and ${stats?.low_stock_count || 0} low stock alerts.`
              : `আপনার ${stats?.pending_jobs || 0}টি অপেক্ষমান জব এবং ${stats?.low_stock_count || 0}টি কম স্টক সতর্কতা আছে।`}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.link} data-testid={`stat-card-${index}`}>
              <Card className="hover:border-blue-500 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 md:w-12 md:h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                      <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Notifications Bar */}
        {(marketingAlerts.due_reminders > 0 || marketingAlerts.pending_requests > 0 || (reminderStats && (reminderStats.pending_total > 0 || reminderStats.sent_this_month > 0))) && (
          <div className="flex flex-wrap gap-3">
            {marketingAlerts.pending_requests > 0 && (
              <Link to={`${basePath}/marketing?tab=campaigns`} className="flex-1 min-w-[260px]">
                <Card className="border-blue-200 bg-blue-50/80 hover:border-blue-400 transition-colors cursor-pointer">
                  <CardContent className="p-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center shrink-0">
                      <MessageSquare className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-blue-800 text-sm">
                        {marketingAlerts.pending_requests} {t('marketing_parts_requests')}
                      </p>
                      <p className="text-xs text-blue-500">
                        {language === 'en' ? 'Review in Marketing Tools → SMS Campaigns' : 'মার্কেটিং টুলস → এসএমএস ক্যাম্পেইনে দেখুন'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
            {(marketingAlerts.due_reminders > 0 || (reminderStats && (reminderStats.pending_total > 0 || reminderStats.sent_this_month > 0))) && (
              <Link to={`${basePath}/marketing?tab=reminders`} className="flex-1 min-w-[260px]">
                <Card className="border-orange-200 bg-orange-50/80 hover:border-orange-400 transition-colors cursor-pointer">
                  <CardContent className="p-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shrink-0">
                      <Bell className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-orange-800 text-sm">
                        {marketingAlerts.due_reminders > 0
                          ? <>{marketingAlerts.due_reminders} {t('marketing_reminders_due')}</>
                          : (language === 'en' ? 'Smart Reminders' : 'স্মার্ট রিমাইন্ডার')}
                      </p>
                      {reminderStats && (
                        <div className="flex items-center gap-1.5 text-xs mt-0.5 flex-wrap">
                          <span className={`font-semibold ${reminderStats.due_today > 0 ? 'text-red-600' : 'text-slate-400'}`}>{reminderStats.due_today} {language === 'en' ? 'due today' : 'আজ ডিউ'}</span>
                          <span className="text-slate-300">·</span>
                          <span className={`font-semibold ${reminderStats.due_this_week > 0 ? 'text-orange-600' : 'text-slate-400'}`}>{reminderStats.due_this_week} {language === 'en' ? 'this week' : 'এই সপ্তাহে'}</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-slate-500">{reminderStats.pending_total} {language === 'en' ? 'pending' : 'অপেক্ষমান'}</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-emerald-600 font-medium">{reminderStats.sent_this_month} {language === 'en' ? 'sent this month' : 'এই মাসে পাঠানো'}</span>
                        </div>
                      )}
                      {!reminderStats && (
                        <p className="text-xs text-orange-500">
                          {language === 'en' ? 'Review in Marketing Tools → Reminders' : 'মার্কেটিং টুলস → রিমাইন্ডারে দেখুন'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        )}

        {/* Quick Actions & Recent Jobs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">{t('quick_actions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to={`${basePath}/jobs/new`} className="block" data-testid="quick-new-job">
                <Button className="w-full justify-start gap-3 h-12 bg-blue-800 hover:bg-blue-900">
                  <Plus className="w-5 h-5" />
                  {t('new_job')}
                </Button>
              </Link>
              <Link to={`${basePath}/customers/new`} className="block" data-testid="quick-new-customer">
                <Button variant="outline" className="w-full justify-start gap-3 h-12">
                  <Users className="w-5 h-5" />
                  {t('new_customer')}
                </Button>
              </Link>
              <Link to={`${basePath}/inventory`} className="block" data-testid="quick-inventory">
                <Button variant="outline" className="w-full justify-start gap-3 h-12">
                  <Package className="w-5 h-5" />
                  {t('nav_inventory')}
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Barlow_Condensed']">{t('recent_jobs')}</CardTitle>
              <Link to={`${basePath}/jobs`} data-testid="view-all-jobs">
                <Button variant="ghost" size="sm" className="gap-1">
                  {language === 'en' ? 'View All' : 'সব দেখুন'}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_jobs?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_jobs.map((job) => (
                    <Link 
                      key={job.id} 
                      to={`${basePath}/jobs/${job.id}`}
                      className="block"
                      data-testid={`recent-job-${job.id}`}
                    >
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-blue-800">{job.job_number}</span>
                            <Badge className={statusColors[job.status]}>
                              {statusLabels[language][job.status]}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 truncate">
                            {job.customer_name} • {job.bike_model}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-semibold text-slate-900">
                            {formatCurrency(job.total_amount, language)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>{language === 'en' ? 'No recent jobs' : 'সাম্প্রতিক কোন জব নেই'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">{t('monthly_revenue')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-600">
                {formatCurrency(stats?.monthly_revenue || 0, language)}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {language === 'en' ? 'This month' : 'এই মাস'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">{t('completed_today')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="text-4xl font-bold text-blue-800">
                  {stats?.completed_today || 0}
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {language === 'en' ? 'Jobs completed today' : 'আজ সম্পন্ন জব'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
